import React from 'react'
import { useCombobox, UseComboboxState } from 'downshift'
import { useTranslation } from 'react-i18next'
import { useId } from '@reach/auto-id'
import { debounce } from 'utils/debounce'
import { Icon } from 'components/Icon'
import styles from './ComboboxInput.module.scss'
import cx from 'classnames'
import { Text } from 'components/Text'
import { Spinner } from 'components/Spinner'

type ComboboxInputProps<T> = {
  name: string
  placeholder: string
  inputItems: T[]
  initialValue?: string
  onChange: (query?: string) => void
  onSelect: (item?: T) => void
  state?: { type: string; message?: string }
  className?: string
  formatter?: (item: T) => string
  menuItem?: (item: T) => JSX.Element
  autoFocus?: boolean
}

export function ComboboxInput<T>({
  name,
  placeholder,
  inputItems,
  state,
  className = '',
  initialValue = '',
  onChange,
  onSelect,
  formatter = (item) => String(item),
  menuItem,
  autoFocus = false,
}: ComboboxInputProps<T>) {
  const inputId = useId()
  const { t } = useTranslation()

  const {
    isOpen,
    highlightedIndex,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    getItemProps,
    inputValue,
  } = useCombobox<T>({
    items: inputItems,
    itemToString: formatter,
    initialInputValue: initialValue,

    // TODO: use 'type' prop once https://github.com/downshift-js/downshift/pull/985 is merged
    onInputValueChange: ({ inputValue, selectedItem }) => {
      let debounceTime = 300

      if (!inputValue) {
        selectedItem && onSelect()
        debounceTime = 0
      } else if (formatter(selectedItem!) === inputValue) return

      debounce(onChange, debounceTime)(inputValue)
    },

    onSelectedItemChange: (change: Partial<UseComboboxState<T>>) => {
      onSelect(change.selectedItem)
    },
  })

  const hasItems = isOpen && inputItems.length > 0
  const classes = cx({
    [styles.comboboxInput]: true,
    [className]: className,
    [styles.errored]: state?.type === 'error',
    [styles.isOpen]: hasItems,
    [styles.filled]: inputValue,
  })

  return (
    <div className={classes}>
      <div {...getComboboxProps()}>
        <input
          {...getInputProps({
            id: inputId,
            name: name,
            autoFocus,
            placeholder,
            'aria-label': placeholder,
          })}
        />

        {state?.type === 'loading' && (
          <Spinner label={t('common.loading')} className={styles.loadingIcon} />
        )}

        {state?.type === 'error' ? (
          <Text size="xsmall" className={styles.error}>
            <Icon name="alert-line" size="lg" /> {state?.message}
          </Text>
        ) : null}
      </div>

      <ul
        {...getMenuProps({
          className: styles.menu,
        })}
      >
        {hasItems &&
          inputItems.map((item, index) => (
            <li
              key={`${item}${index}`}
              {...getItemProps({
                item,
                index,
                className: cx({
                  [styles.menuItem]: true,
                  [styles.highlighted]: highlightedIndex === index,
                }),
              })}
            >
              {menuItem ? menuItem(item) : formatter(item)}
            </li>
          ))}
      </ul>
    </div>
  )
}

ComboboxInput.displayName = 'ComboboxInput'
